import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bell, ChevronLeft, ChevronDown, ChevronRight, Check, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function Notifications() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/me/notifications-by-building?limit_per_group=20`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setGroups(d.groups || []);
      setTotalUnread(d.total_unread || 0);
      // Auto-expand groups with unread
      const e = new Set((d.groups || []).filter((g) => g.unread > 0).map((g) => g.hoa_id));
      setExpanded(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const toggle = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  const markRead = async (notification_id) => {
    await fetch(`${API}/api/me/notifications/${notification_id}/read`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    fetchNotifications();
  };

  const markAllRead = async (hoa_id) => {
    const url = hoa_id ? `${API}/api/me/notifications/mark-all-read?hoa_id=${encodeURIComponent(hoa_id)}` : `${API}/api/me/notifications/mark-all-read`;
    const r = await fetch(url, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (r.ok) {
      toast.success('Marked as read');
      fetchNotifications();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Bell className="w-7 h-7 text-indigo-600" /> Notifications
              {totalUnread > 0 && (
                <Badge className="bg-red-600 text-white" data-testid="total-unread">{totalUnread}</Badge>
              )}
            </h1>
            <p className="text-gray-600 text-sm">Grouped by building · {groups.length} group(s)</p>
          </div>
          {totalUnread > 0 && (
            <Button variant="outline" size="sm" onClick={() => markAllRead()} data-testid="mark-all-read-btn">
              <Check className="w-4 h-4 mr-1" /> Mark all read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : groups.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-500">
            <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No notifications yet.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const isOpen = expanded.has(g.hoa_id);
              const title = g.hoa_id === 'general' ? 'General' : (g.building_name || g.hoa_id);
              return (
                <Card key={g.hoa_id} data-testid={`notif-group-${g.hoa_id}`}>
                  <CardContent className="p-0">
                    <button
                      onClick={() => toggle(g.hoa_id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                      data-testid={`group-header-${g.hoa_id}`}
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Building2 className="w-5 h-5 text-indigo-600" />
                      <span className="font-semibold flex-1">{title}</span>
                      {g.unread > 0 && (
                        <Badge className="bg-red-600 text-white" data-testid={`unread-${g.hoa_id}`}>{g.unread}</Badge>
                      )}
                      <span className="text-xs text-gray-500">{g.items.length} item(s)</span>
                    </button>
                    {isOpen && (
                      <div className="border-t divide-y">
                        {g.unread > 0 && (
                          <div className="px-4 py-2 text-right">
                            <button
                              onClick={() => markAllRead(g.hoa_id)}
                              className="text-xs text-indigo-600 hover:underline"
                              data-testid={`mark-group-read-${g.hoa_id}`}
                            >
                              Mark group as read
                            </button>
                          </div>
                        )}
                        {g.items.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">No notifications</div>
                        ) : g.items.map((n) => (
                          <div
                            key={n.notification_id}
                            className={`px-4 py-3 ${!n.read ? 'bg-indigo-50/50' : ''}`}
                            data-testid={`notif-${n.notification_id}`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <div className="font-medium text-sm">{n.title}</div>
                                <div className="text-sm text-gray-600 mt-0.5">{n.body}</div>
                                <div className="text-xs text-gray-400 mt-1">
                                  {new Date(n.created_at).toLocaleString()}
                                </div>
                              </div>
                              {!n.read && (
                                <button
                                  onClick={() => markRead(n.notification_id)}
                                  className="text-xs text-indigo-600 hover:underline shrink-0"
                                  data-testid={`mark-read-${n.notification_id}`}
                                >
                                  Mark read
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
